'use client';

import { motion, useReducedMotion } from 'framer-motion';

export type MeridianState = 'idle' | 'loading' | 'success' | 'empty';

interface MeridianProps {
  state?: MeridianState;
  className?: string;
  width?: number;
}

const nodes = {
  wingtipL: { x: 38, y: 120, r: 4 },
  wingtipR: { x: 344, y: 118, r: 4 },
  head: { x: 196, y: 96, r: 4, color: 'green' },
  heart: { x: 190, y: 135, r: 4.4, color: 'green' },
  beak: { x: 214, y: 100, r: 2.2, color: 'amber' },
  tail: { x: 182, y: 168, r: 3, color: 'cyan' },
  center: { x: 172, y: 115, r: 3.2, color: 'cyan' },
  center2: { x: 210, y: 118, r: 3.2, color: 'cyan' },
};

const wingL = [
  { x: 75, y: 100 }, { x: 115, y: 92 }, { x: 150, y: 100 },
  { x: 90, y: 122 }, { x: 130, y: 118 },
];
const wingR = [
  { x: 308, y: 98 }, { x: 272, y: 90 }, { x: 235, y: 102 },
  { x: 290, y: 118 }, { x: 250, y: 122 },
];

const lines = [
  'M38 120 L75 100 L115 92 L150 100 L172 115',
  'M344 118 L308 98 L272 90 L235 102 L210 118',
  'M38 120 L90 122 L130 118 L172 115',
  'M344 118 L290 118 L250 122 L210 118',
  'M75 100 L90 122 M115 92 L130 118 M150 100 L130 118',
  'M308 98 L290 118 M272 90 L250 122 M235 102 L250 122',
  'M172 115 L190 135 L210 118',
  'M172 115 L196 96 L210 118',
  'M196 96 L214 100',
  'M190 135 L182 168',
  'M182 168 L172 115 M182 168 L210 118',
];

const colorMap: Record<string, string> = {
  cyan: '#22d3ee',
  green: '#4ade80',
  amber: '#fbbf24',
};

export function Meridian({ state = 'idle', className, width = 200 }: MeridianProps) {
  const reduceMotion = useReducedMotion();
  const opacity = state === 'empty' ? 0.3 : 1;
  const animate = !reduceMotion && state !== 'empty';

  return (
    <svg
      width={width}
      viewBox="0 0 380 260"
      role="img"
      aria-label="Meridian"
      className={className}
    >
      <title>Meridian</title>
      <g opacity={opacity}>
        <g stroke="#22d3ee" strokeWidth="0.8" opacity="0.65" fill="none">
          {lines.map((d, i) => (
            <motion.path
              key={i}
              d={d}
              initial={state === 'loading' && animate ? { pathLength: 0 } : undefined}
              animate={
                state === 'loading' && animate
                  ? { pathLength: 1 }
                  : undefined
              }
              transition={{ duration: 0.6, delay: i * 0.05 }}
            />
          ))}
        </g>

        {[...wingL, ...wingR].map((n, i) => (
          <circle key={i} cx={n.x} cy={n.y} r={2.6} fill={colorMap.cyan} />
        ))}

        {Object.entries(nodes).map(([key, n]) => (
          <motion.circle
            key={key}
            cx={n.x}
            cy={n.y}
            r={n.r}
            fill={colorMap[('color' in n && n.color) || 'cyan']}
            animate={
              animate && state === 'idle'
                ? { opacity: [1, 0.6, 1] }
                : state === 'success' && key === 'heart'
                ? { scale: [1, 1.8, 1], opacity: [1, 0.4, 1] }
                : undefined
            }
            transition={{
              duration: state === 'success' ? 0.8 : 2.4,
              repeat: state === 'idle' ? Infinity : 0,
              delay: state === 'idle' ? Math.random() * 1.5 : 0,
            }}
          />
        ))}
      </g>
    </svg>
  );
}
